import React, { useState, useEffect } from 'react'
import { NavBar, Form, Input, Button, Tabs, Cell, Tag, Toast, PullRefresh, List, Empty, ActionSheet } from 'react-vant'
import { ArrowLeft, ChatO, QuestionO, WarningO, InfoO } from '@react-vant/icons'
import { useNavigate } from 'react-router-dom'
import { submitFeedback, getMyFeedbackList } from '../../api/message'

const CATEGORIES = [
    { label: '缺陷反馈 (Bug)', value: 'bug', icon: <WarningO color="#e11d48" /> },
    { label: '改进建议', value: 'suggestion', icon: <InfoO color="#0891b2" /> },
    { label: '业务咨询', value: 'consult', icon: <QuestionO color="#d97706" /> },
    { label: '其他', value: 'other', icon: <ChatO color="#00b578" /> },
]

const STATUS_MAP = {
    pending: { text: '待处理', color: 'default' },
    replied: { text: '已回复', color: 'warning' },
    resolved: { text: '已解决', color: 'success' },
    closed: { text: '已关闭', color: 'default' }
}

export default function Feedback() {
    const navigate = useNavigate()
    const [activeTab, setActiveTab] = useState('submit')
    const [form] = Form.useForm()
    const [selectedCategory, setSelectedCategory] = useState('suggestion')

    // 历史列表状态
    const [list, setList] = useState([])
    const [loading, setLoading] = useState(false)
    const [finished, setFinished] = useState(false)
    const [page, setPage] = useState(1)
    const [refreshing, setRefreshing] = useState(false)

    const handleCategorySelect = (val) => {
        setSelectedCategory(val)
        form.setFieldsValue({ category: val })
    }

    // 提交逻辑
    const onFinish = async (values) => {
        try {
            await submitFeedback({ ...values, category: selectedCategory })
            Toast.success('提交成功，我们会尽快处理')
            form.resetFields()
            setSelectedCategory('suggestion')
            setActiveTab('history')
            onRefresh()
        } catch (error) {
            Toast.fail(error.response?.data?.detail || '提交失败')
        }
    }

    // 加载列表
    const onLoad = async (isRefresh = false) => {
        try {
            setLoading(true)
            const currentPage = isRefresh ? 1 : page
            const res = await getMyFeedbackList({ page: currentPage, page_size: 10 })

            const newList = isRefresh ? res.items : [...list, ...res.items]
            setList(newList)
            setFinished(newList.length >= res.total)
            setPage(currentPage + 1)
        } catch (error) {
            Toast.fail('加载列表失败')
        } finally {
            setLoading(false)
            setRefreshing(false)
        }
    }

    const onRefresh = () => {
        setRefreshing(true)
        onLoad(true)
    }

    useEffect(() => {
        if (activeTab === 'history') {
            onRefresh()
        }
    }, [activeTab])

    return (
        <div className="feedback-page" style={{ minHeight: '100vh', background: '#f8fafc' }}>
            <NavBar
                title="意见反馈"
                leftText={<ArrowLeft />}
                onClickLeft={() => navigate(-1)}
                fixed
                placeholder
            />

            <Tabs active={activeTab} onChange={setActiveTab} sticky offsetTop={46} color="#00b578">
                <Tabs.TabPane name="submit" title="提交反馈">
                    <div style={{ padding: '16px 12px' }}>
                        <div style={{ background: '#fff', borderRadius: 12, padding: '4px 0', boxShadow: '0 2px 12px rgba(0,0,0,0.03)' }}>
                            <Form
                                form={form}
                                onFinish={onFinish}
                                layout="vertical"
                                footer={
                                    <div style={{ margin: '32px 16px 16px' }}>
                                        <Button round block type="primary" nativeType="submit" style={{ height: 46 }}>
                                            提交反馈
                                        </Button>
                                    </div>
                                }
                            >
                                <Form.Item
                                    name="category"
                                    label="反馈类型"
                                    rules={[{ required: true, message: '请选择反馈类型' }]}
                                    initialValue="suggestion"
                                >
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                                        {CATEGORIES.map(cat => (
                                            <div
                                                key={cat.value}
                                                onClick={() => handleCategorySelect(cat.value)}
                                                style={{
                                                    padding: '12px',
                                                    borderRadius: 8,
                                                    border: `1px solid ${selectedCategory === cat.value ? '#00b578' : '#e2e8f0'}`,
                                                    background: selectedCategory === cat.value ? '#f0fdf4' : '#fff',
                                                    textAlign: 'center',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: 6
                                                }}
                                            >
                                                {cat.icon}
                                                <span style={{ fontSize: 13, color: selectedCategory === cat.value ? '#00b578' : '#0f172a' }}>{cat.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                </Form.Item>
                                <Form.Item
                                    name="title"
                                    label="反馈标题"
                                    rules={[{ required: true, message: '请输入标题' }]}
                                >
                                    <Input placeholder="简要描述您的问题" />
                                </Form.Item>
                                <Form.Item
                                    name="content"
                                    label="详细说明"
                                    rules={[{ required: true, message: '请输入详细内容' }]}
                                >
                                    <Input
                                        type="textarea"
                                        rows={6}
                                        placeholder="请详细描述您遇到的问题或改进建议，以便我们更好地为您服务..."
                                        showWordLimit
                                        maxLength={500}
                                    />
                                </Form.Item>
                            </Form>
                        </div>

                        <div style={{ marginTop: 16, padding: '0 8px', fontSize: 13, color: '#94a3b8', lineHeight: 1.6 }}>
                            <p>温馨提示：</p>
                            <p>1. 如果是紧急业务问题，请优先联系校方管理员。</p>
                            <p>2. 反馈提交后，您可以在“历史记录”中查看处理进度。</p>
                        </div>
                    </div>
                </Tabs.TabPane>

                <Tabs.TabPane name="history" title="历史记录">
                    <PullRefresh onRefresh={onRefresh} refreshing={refreshing}>
                        <List
                            loading={loading}
                            finished={finished}
                            onLoad={() => onLoad()}
                            errorText="请求失败，点击重新加载"
                        >
                            {list.length > 0 ? list.map(item => (
                                <div key={item.id} style={{
                                    margin: '12px 12px 0',
                                    background: '#fff',
                                    borderRadius: 12,
                                    overflow: 'hidden',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                                }}>
                                    <Cell
                                        title={
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span style={{ fontWeight: 600 }}>{item.title}</span>
                                                <Tag type={STATUS_MAP[item.status]?.color || 'default'} size="medium" plain>
                                                    {STATUS_MAP[item.status]?.text || item.status}
                                                </Tag>
                                            </div>
                                        }
                                        label={`${new Date(item.created_at).toLocaleString()} | ${item.category}`}
                                    />
                                    <div style={{ padding: '0 16px 16px', fontSize: 14, color: '#475569' }}>
                                        <div style={{ background: '#f8fafc', padding: 12, borderRadius: 8 }}>
                                            {item.content}
                                        </div>
                                        {item.reply_content && (
                                            <div style={{
                                                marginTop: 12,
                                                borderTop: '1px dashed #e2e8f0',
                                                paddingTop: 12
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#00b578', marginBottom: 6, fontSize: 13, fontWeight: 600 }}>
                                                    <ChatO /> 管理员回复：
                                                </div>
                                                <div style={{ color: '#0f172a', lineHeight: 1.5 }}>
                                                    {item.reply_content}
                                                </div>
                                                <div style={{ textAlign: 'right', fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                                                    {new Date(item.replied_at).toLocaleString()}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )) : !loading && <Empty description="暂无历史纪录" />}
                        </List>
                    </PullRefresh>
                </Tabs.TabPane>
            </Tabs>
        </div>
    )
}
